import React, { Component } from 'react';
import { connect } from 'react-redux';
import MANAGED_UX_FORMS_CONTROLLER from '../../../controllers/managedUxForms';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import DocumentItem from './DocumentItem';
import FailedDocumentDetails from '../rowDetails/FailedDocumentDetails';
import ChildrenDetails from '../rowDetails/ChildrenDetails';
import { NON_ACTIONABLE_STATUSES, DetailsRowContentType, resolveDetailsRowContentType, resolveStatusKey } from '../../../utils/uploadedDocumentsHelper';
import _ from 'underscore';
import DocumentDetails from './DocumentDetails';
import NoData from '../../page/NoData';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import ReadOnlyWrapper from '../../authorization/ReadOnlyWrapper';
import { STableContainer, SPagination, SAlert } from '@avalara/skylab-sdk/react';
import BulkOperationActionPanel from '../bulkOperations/BulkOperationActionPanel';

const FORM_NAME = 'UPLOADED_DOCUMENT_LIST_FORM';
const DEFAULT_ROWS_PER_PAGE = 20;
const log = new Logger('UPLOADED_DOCUMENT_LIST_FORM', LOG_LEVELS.DEBUG);

export class DocumentListForm extends Component {

    constructor(props) {
        super(props);
        this.state = {
            documentDetailsOpen: false,
            detailsRowExpandedMap: {},
            pagingConfig: { startIndex: 0, endIndex: DEFAULT_ROWS_PER_PAGE - 1, rowsPerPage: DEFAULT_ROWS_PER_PAGE }
        };
        this.callbackCache = {};
    }
    
    getCachedCallback(key, factory) {
        if (!this.callbackCache[key]) {
            this.callbackCache[key] = factory();
        }
        return this.callbackCache[key];
    }

    onDetailsRowExpanderClickFactory(documentId) {
        return (e) => {
            const expanded = e.currentTarget.expanded;
            this.setState((prevState) => ({
                detailsRowExpandedMap: { ...prevState.detailsRowExpandedMap, [documentId]: expanded }
            }));
        };
    }

    renderDetailsRowComponent(contentType, document, rowKey = document.id) {
        const fieldValues = this.props.formData && this.props.formData.fieldValues;

        switch (contentType) {
            case DetailsRowContentType.FailureDetails:
                return this.state.detailsRowExpandedMap[rowKey] ? <FailedDocumentDetails fileId={document.id} languages={this.props.languages} /> : null;
            case DetailsRowContentType.ChildRows:
                return this.state.detailsRowExpandedMap[rowKey] ? (
                    <ChildrenDetails
                        childRows={document.childRows}
                        fieldValues={fieldValues}
                        languages={this.props.languages}
                        parentDocument={document}
                        getOnSelect={(childId) => this.getCachedCallback(`onSelect-${childId}`, () => this.onSelectDocumentFactory(childId).bind(this))}
                        getLoadDocumentDetails={(childId) => this.getCachedCallback(`loadDocumentDetails-${childId}`, () => this.onSelectDocumentDetailFactory(childId).bind(this))}
                    />
                ) : null;
            default:
                return <div className="details-row-unavailable"><LanguageStringResolver string="uploadedDocuments_noDetailsAvailable" /></div>;
        }
    }

    renderDetailsRow(document, contentType, rowKey = document.id) {
        return (
            <tbody id={`document-details-row-${rowKey}`} className="details-row hidden">
                <tr>
                    <td colSpan={1} className="expander-column"></td>
                    <td colSpan={7} className="filename-column">
                        {this.renderDetailsRowComponent(contentType, document, rowKey)}
                    </td>
                </tr>
            </tbody>
        );
    }

    hasActionableStatus(processingStatus) {
        const statusKey = resolveStatusKey(processingStatus);
        return !NON_ACTIONABLE_STATUSES.has(statusKey);
    }

    onPaginate(e) {
        this.setState({
            pagingConfig: {
                startIndex: e.detail.startIndex,
                endIndex: e.detail.endIndex,
                rowsPerPage: e.detail.rowsPerPage
            }
        });
        this.clearAllSelections();
    }

    getPagedDocumentList() {
        const documentList = this.props.documentList;
        if (!documentList) return [];
        const { startIndex, endIndex } = this.state.pagingConfig;
        return documentList.slice(startIndex, endIndex + 1);
    }

    getActionableIdsForList(list) {
        if (!list) return [];
        const ids = [];
        list.forEach((document) => {
            if (document.id === null) return;
            if (this.hasActionableStatus(document.processingStatus)) {
                ids.push(document.id);
            }
            if (document.childRows) {
                document.childRows.forEach((childDoc) => {
                    if (childDoc.id !== null && this.hasActionableStatus(childDoc.processingStatus)) {
                        ids.push(childDoc.id);
                    }
                });
            }
        });
        return ids;
    }

    onSelectPage(select) {
        const ids = this.getActionableIdsForList(this.getPagedDocumentList());
        if (ids.length === 0) return;
        const fieldValues = ids.reduce((values, id) => {
            values[id + ''] = select;
            return values;
        }, {});
        this.props.doFormBulkChange && this.props.doFormBulkChange(fieldValues);
    }

    isPageFullySelected() {
        const fieldValues = this.props.formData && this.props.formData.fieldValues;
        if (!fieldValues) return false;
        const ids = this.getActionableIdsForList(this.getPagedDocumentList());
        return ids.length > 0 && ids.every(id => fieldValues[id + '']);
    }

    onSelectAll(select) {
        const documentList = this.props.documentList;
        log.debug('onSelectAll(): ', select, documentList);
        if (!documentList) {
            return;
        }
        const fieldValues = documentList.reduce((values, document) => {
            if (document.id === null) {
                return values;
            }

            if (this.hasActionableStatus(document.processingStatus)) {
                values[document.id + ''] = select;
            }

            if (document.childRows) {
                document.childRows.forEach(childDoc => {
                    if (childDoc.id !== null && this.hasActionableStatus(childDoc.processingStatus)) {
                        values[childDoc.id + ''] = select;
                    }
                });
            }

            return values;
        }, { '_all': select });
        this.props.doInitialiseForm && this.props.doInitialiseForm(fieldValues);
    }

    clearAllSelections() {
        this.onSelectAll(false);
    }

    findParentDocumentByChildId(childId) {
        const documentList = this.props.documentList;
        if (!documentList) return null;

        for (const parentDoc of documentList) {
            if (parentDoc.childRows) {
                const childExists = parentDoc.childRows.some(child => child.id === childId);
                if (childExists) {
                    return parentDoc;
                }
            }
        }
        return null;
    }

    getChildIds(parentId) {
        const documentList = this.props.documentList;
        if (!documentList) return [];

        const parentDoc = documentList.find(doc => doc.id === parentId);
        if (!parentDoc || !parentDoc.childRows) return [];

        return parentDoc.childRows.map(child => child.id);
    }

    clearSelectAllIfDeselecting(select) {
        if (!select && this.props.formData?.fieldValues?.['_all']) {
            this.props.onFieldChange?.('_all', false);
        }
    }

    selectDocumentWithChildren(documentId, select) {
        this.props.onFieldChange?.(documentId + '', select);
        this.getChildIds(documentId).forEach(childId => {
            this.props.onFieldChange?.(childId + '', select);
        });
    }

    selectChildAndUpdateParent(documentId, select) {
        this.props.onFieldChange?.(documentId + '', select);
        const parentDoc = this.findParentDocumentByChildId(documentId);
        if (!parentDoc) return;

        const fieldValues = this.props.formData?.fieldValues;
        if (!fieldValues) return;

        const childIds = this.getChildIds(parentDoc.id);
        const allChildrenSelected = select && childIds.every(
            childId => childId === documentId || fieldValues[childId + '']
        );
        this.props.onFieldChange?.(parentDoc.id + '', allChildrenSelected);
    }

    onSelectDocumentFactory(documentId) {
        return (select, _id = documentId, isParent = false) => {
            this.clearSelectAllIfDeselecting(select);
            if (isParent || this.getChildIds(documentId).length > 0) {
                this.selectDocumentWithChildren(documentId, select);
            } else {
                this.selectChildAndUpdateParent(documentId, select);
            }
        };
    }

    onSelectDocumentDetailFactory(documentId) {
        return () => {
            log.debug('onSelectDocumentDetailFactory(%s): ', documentId);
            if (this.props.loadDocumentDetails) {
                this.props.loadDocumentDetails(documentId);
                this.setState({ documentDetailsOpen: true });
            }
        };
    }

    onCloseDocumentDetails() {
        log.debug('onCloseDocumentDetails: ');
        if (this.props.UnloadDocumentDetails) {
            this.props.UnloadDocumentDetails();
        }
        this.setState({ documentDetailsOpen: false });
    }

    onBulkActionSubmitted() {
        this.props.doFormSubmitted && this.props.doFormSubmitted();
        this.clearAllSelections();
    }

    getSelectedIds() {
        const documentList = this.props.documentList;
        const fieldValues = this.props.formData && this.props.formData.fieldValues;

        if (!fieldValues) return [];

        const documentIdsToAction = [];

        _.each(documentList, (document) => {
            if (document.childRows) {
                _.each(document.childRows, (childDoc) => {
                    if (fieldValues[childDoc.id + '']) {
                        documentIdsToAction.push({ id: childDoc.id, fileName: childDoc.fileName });
                    }
                });
            }
        });

        return documentIdsToAction;
    }

    renderContent(documentList, fieldValues, selectedIds) {
        const isEmpty = !documentList || documentList.length === 0;
        const pagedDocumentList = this.getPagedDocumentList();
        const pageFullySelected = this.isPageFullySelected();
        const hasSelectableEntriesOnPage = this.getActionableIdsForList(pagedDocumentList).length > 0;

        return (
            <div>
                <div className="uploaded-documents-bulk-action-panel-top">
                    <BulkOperationActionPanel
                        documentList={documentList}
                        selectedIds={selectedIds}
                        onRequestAction={this.props.onRequestAction}
                        onActionSubmitted={this.onBulkActionSubmitted.bind(this)}
                        showMessages={false}
                    />
                </div>
                {pageFullySelected && (
                    <SAlert status="info" noDismiss role="alert" className='mb10'>
                        <LanguageStringResolver string="uploadedDocuments_allSelectableOnPageSelected" />
                    </SAlert>
                )}
                <STableContainer>
                    <table className="word-break-all document-table uploaded-documents-table">
                        <colgroup>
                            <col className="expander-column" />
                            <col className="filename-column" />
                            <col className="date-column" />
                            <col className="code-column" />
                            <col className="name-column" />
                            <col className="status-column" />
                            <col className="status-column" />
                            <col className="checkbox-column" />
                        </colgroup>
                        <thead>
                            <DocumentItem
                                header={true}
                                formInitialising={!fieldValues}
                                onSelect={this.onSelectPage.bind(this)}
                                selected={this.isPageFullySelected()}
                                hasSelectableEntries={hasSelectableEntriesOnPage}
                            />
                        </thead>
                        {isEmpty && (
                            <tbody>
                                <tr data-testid="uploaded-documents-no-data-row">
                                    <td colSpan="8">
                                        <div className="uploaded-documents-table-no-data" data-testid="uploaded-documents-no-data">
                                            <NoData noDataText="processedDocuments_NoData" />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                        {pagedDocumentList.map((document, index) => {
                            if (document.id === null) {
                                return (
                                    <tbody key={`upload-${document.fileName}`}>
                                        <DocumentItem
                                            formInitialising={!fieldValues}
                                            document={document}
                                        />
                                    </tbody>
                                );
                            }

                            const hasChildRows = document.childRows && document.childRows.length > 0;
                            const contentType = resolveDetailsRowContentType(document);
                            const canExpandDetailsRow = contentType !== DetailsRowContentType.None;
                            const rowKey = `${document.id}-${index}`;

                            return (
                                <React.Fragment key={rowKey}>
                                    <tbody>
                                        <DocumentItem
                                            formInitialising={!fieldValues}
                                            document={document}
                                            onSelect={this.getCachedCallback(`onSelect-${document.id}`, () => this.onSelectDocumentFactory(document.id).bind(this))}
                                            selected={fieldValues && fieldValues[document.id + '']}
                                            hasChildRows={hasChildRows}
                                            canExpandDetailsRow={canExpandDetailsRow}
                                            detailsRowId={`document-details-row-${rowKey}`}
                                            onDetailsRowExpanderClick={this.getCachedCallback(`onDetailsRowExpanderClick-${rowKey}`, () => this.onDetailsRowExpanderClickFactory(rowKey).bind(this))}
                                        />
                                    </tbody>
                                    {canExpandDetailsRow && this.renderDetailsRow(document, contentType, rowKey)}
                                </React.Fragment>
                            );
                        })}
                    </table>
                </STableContainer>
                <SPagination
                    className="mt20 mb20 uploaded-documents-pagination"
                    totalRecords={(documentList && documentList.length) || 0}
                    startIndex={this.state.pagingConfig.startIndex}
                    rowsPerPage={this.state.pagingConfig.rowsPerPage}
                    onS-paginate={this.onPaginate.bind(this)}
                />
                <BulkOperationActionPanel
                    documentList={documentList}
                    selectedIds={selectedIds}
                    onRequestAction={this.props.onRequestAction}
                    onActionSubmitted={this.onBulkActionSubmitted.bind(this)}
                />
                <hr />
                <div>
                    <DocumentDetails
                        documentDetails={this.props.documentDetails}
                        error={this.props.documentDetailsError}
                        onClose={this.onCloseDocumentDetails.bind(this)}
                        open={this.state.documentDetailsOpen} />
                </div>
            </div>
        );
    }

    render() {
        log.debug('render(): ', this.props);
        const documentList = this.props.documentList;
        const fieldValues = this.props.formData && this.props.formData.fieldValues;
        const selectedIds = fieldValues ? this.getSelectedIds() : [];

        return (
            <ReadOnlyWrapper>
                <div>
                    {this.renderContent(documentList, fieldValues, selectedIds)}
                </div>
            </ReadOnlyWrapper>
        );
    }

    componentDidUpdate(prevProps) {
        if (prevProps.documentList !== this.props.documentList) {
            this.callbackCache = {};
        }

        const prevLength = (prevProps.documentList && prevProps.documentList.length) || 0;
        const currentLength = (this.props.documentList && this.props.documentList.length) || 0;
        if (prevLength !== currentLength) {
            this.setState({
                pagingConfig: {
                    startIndex: 0,
                    endIndex: this.state.pagingConfig.rowsPerPage - 1,
                    rowsPerPage: this.state.pagingConfig.rowsPerPage
                }
            });
        }
    }

    componentWillUnmount() {
        log.debug('componentWillUnmount(): ', this.props);
        this.props.doDestroyForm && this.props.doDestroyForm();
    }

    UNSAFE_componentWillMount() {
        log.debug('componentWillMount(): ', this.props);
        this.onSelectAll(this.props.initialSelection);
    }
}


DocumentListForm.defaultProps = {
    languages: {},
    initialSelection: false,
    onRequestAction: null,
    loadDocumentDetails: null,
    UnloadDocumentDetails: null
};

function mapStateToProps(state) {
    log.debug('mapStateToProps(): state', state);
    return {
        formData: (state.managedUxForms && state.managedUxForms.forms[FORM_NAME]),
        languages: state.languages || {},
    };
}

function mapDispatchersToProps(dispatch) {
    return {
        doInitialiseForm: (initialValues) => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doInitialiseForm(FORM_NAME, initialValues));
        },
        doResetForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doResetFormFieldsToInitialValues(FORM_NAME));
        },
        doClearForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doClearFormFields(FORM_NAME));
        },
        onFieldChange: (fieldName, value) => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doFormFieldChange(FORM_NAME, fieldName, value));
        },
        doFormBulkChange: (fieldValues) => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doFormBulkChange(FORM_NAME, fieldValues));
        },
        doFormSubmitted: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doFormSubmitted(FORM_NAME));
        },
        doDestroyForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doRemoveForm(FORM_NAME));
        }
    };
}

export default connect(mapStateToProps, mapDispatchersToProps, null, { withRef: true })(DocumentListForm);
